import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import { Anchor, Menu, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const LANGS = [
  { code: "it", label: "IT" },
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
];

export function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const goSection = (id) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const navItems = [
    { id: "products", label: t.nav.products },
    { id: "producers", label: t.nav.producers },
    { id: "logistics", label: t.nav.logistics },
    { id: "certifications", label: t.nav.certifications },
    { id: "about", label: t.nav.about },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-background/95 backdrop-blur-md border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-20 mx-auto flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-2 group"
        >
          <Anchor
            strokeWidth={1.4}
            className={`h-5 w-5 transition-colors ${
              scrolled ? "text-primary" : "text-primary-foreground"
            }`}
          />
          <span
            className={`font-display text-xl md:text-2xl tracking-tight transition-colors ${
              scrolled ? "text-primary" : "text-primary-foreground"
            }`}
          >
            World Commodity Supply
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {navItems.map((it) => (
            <button
              key={it.id}
              data-testid={`nav-${it.id}`}
              onClick={() => goSection(it.id)}
              className={`text-sm tracking-wide transition-colors hover:text-secondary ${
                scrolled ? "text-foreground" : "text-primary-foreground/90"
              }`}
            >
              {it.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="lang-switcher-trigger"
              className={`text-xs tracking-eyebrow uppercase border px-3 py-2 transition-colors ${
                scrolled
                  ? "border-border text-foreground hover:bg-accent"
                  : "border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10"
              }`}
            >
              {LANGS.find((l) => l.code === lang)?.label || "IT"}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none min-w-[6rem]">
              {LANGS.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className="rounded-none tracking-eyebrow text-xs uppercase"
                >
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            data-testid="header-cta-contact"
            onClick={() => goSection("contact")}
            className={`hidden md:inline-block text-sm px-5 py-2.5 transition-colors ${
              scrolled
                ? "bg-primary text-primary-foreground hover:bg-[#2B1E17]"
                : "bg-primary-foreground text-primary hover:bg-secondary hover:text-secondary-foreground"
            }`}
          >
            {t.nav.contact}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 ${
              scrolled ? "text-foreground" : "text-primary-foreground"
            }`}
            aria-label="menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-background border-t border-border px-6 py-6 space-y-4"
        >
          {navItems.map((it) => (
            <button
              key={it.id}
              data-testid={`mobile-nav-${it.id}`}
              onClick={() => goSection(it.id)}
              className="block w-full text-left text-foreground text-base"
            >
              {it.label}
            </button>
          ))}
          <button
            data-testid="mobile-cta-contact"
            onClick={() => goSection("contact")}
            className="block w-full text-left bg-primary text-primary-foreground px-5 py-3"
          >
            {t.nav.contact}
          </button>
        </div>
      )}
    </header>
  );
}
